import { groupDuplicates } from '@h5s/util-array';
import { Department, VatRate } from '../../settings/entities';
import { mapNaturaTypeToZPrinterVatType, resolveExpectedVatIndex } from './z-printer-natura-mapping';
import { ZPrinterManufacturer } from './z-printer-paper-manufacturer';

/**
 * Conflitti individuabili in memoria, prima di toccare la stampante.
 * Ogni voce è già pronta da mostrare all'utente (es. "codice 2 (Camere, Extra)"); un elenco vuoto vuol dire nessun conflitto.
 */
export interface FiscalConfigConflicts {
  /** Reparti che finirebbero sullo stesso indice della stampante */
  departmentCodes: string[];
  /** Aliquote che finirebbero sulla stessa casella IVA della stampante */
  vatIndexes: string[];
  /** Aliquote con la stessa percentuale, dove la marca non le ammette */
  vatPercentages: string[];
}

/**
 * Aliquote usate da almeno un reparto, senza ripetizioni.
 *
 * Sono le uniche che contano per la stampante: l'IVA di una riga la decide il reparto con cui è programmata (vedi buildItemVatFields),
 * quindi un'aliquota non collegata a nessun reparto non verrebbe mai usata in vendita.
 * È anche lo stesso insieme che usa la verifica della configurazione, così invio e verifica non possono divergere.
 */
export function getVatRatesUsedByDepartments(departments: Department[]): VatRate[] {
  const vatRatesById = new Map<number, VatRate>();

  for (const department of departments) {
    if (!vatRatesById.has(department.vatRate.id)) {
      vatRatesById.set(department.vatRate.id, department.vatRate);
    }
  }

  return [...vatRatesById.values()];
}

const joinNames = (items: { description: string }[]): string => items.map((i) => i.description).join(', ');

/** Vero se tutte le aliquote sono nature e rappresentano la stessa natura sulla stampante */
function isSingleNatura(vatRates: VatRate[]): boolean {
  const naturaTypes = vatRates.map((v) =>
    v.naturaTypeOnZeroVat ? mapNaturaTypeToZPrinterVatType(v.naturaTypeOnZeroVat) : undefined,
  );

  return naturaTypes[0] !== undefined && naturaTypes.every((type) => type === naturaTypes[0]);
}

/**
 * Cerca in memoria le configurazioni che la stampante non potrebbe rappresentare.
 *
 * L'invio esegue prima una chiusura fiscale irreversibile e solo dopo ZBridge può rifiutare la configurazione:
 * scoprire qui il problema evita di "bruciare" una chiusura per niente.
 *
 * @param departments Reparti gestiti da ZStay
 * @param vatRates Aliquote usate dai reparti (vedi getVatRatesUsedByDepartments)
 * @param manufacturer Marca della stampante collegata
 */
export function findFiscalConfigConflicts(
  departments: Department[],
  vatRates: VatRate[],
  manufacturer: ZPrinterManufacturer,
): FiscalConfigConflicts {
  // Due reparti sullo stesso codice finirebbero sullo stesso indice, e uno scavalcherebbe l'altro.
  // Si confronta il valore numerico, come fa l'invio: "01" e "1" sono lo stesso indice.
  const departmentCodes = [
    ...groupDuplicates(departments, (d) => {
      const code = parseInt(d.cashRegisterCode ?? '');
      return Number.isNaN(code) ? undefined : String(code);
    }),
  ].map(([code, group]) => `codice ${code} (${joinNames(group)})`);

  // Una casella IVA della stampante contiene una sola aliquota. Si esclude il caso in cui tutte le aliquote del gruppo
  // siano la stessa natura: è voluto, perché più sottonature (es. N3.1 e N3.2) collassano sulla stessa casella.
  const vatIndexes = [...groupDuplicates(vatRates, (v) => resolveExpectedVatIndex(v, manufacturer)?.toString())]
    .filter(([, group]) => !isSingleNatura(group))
    .map(([index, group]) => `indice ${index} (${joinNames(group)})`);

  // Fonte: documentazione ZBridge ("Vincoli prima di riprogrammare"): su Epson due aliquote con la stessa percentuale
  // fanno rispondere la stampante con errore 13. Non verificato su hardware.
  const vatPercentages =
    manufacturer === ZPrinterManufacturer.Epson
      ? [
          ...groupDuplicates(
            vatRates.filter((v) => !v.naturaTypeOnZeroVat),
            (v) => v.rate.times(100).toFixed(2),
          ),
        ].map(([percentage, group]) => `${percentage}% (${joinNames(group)})`)
      : [];

  return { departmentCodes, vatIndexes, vatPercentages };
}
